import { toast } from '@buffer-mono/popcorn'
import type { ToastAction } from '@buffer-mono/popcorn'

import { routerNavigate } from '~publish/helpers/routerNavigate'
import { refetchAllPostCounts } from '~publish/services/postCounts'

import events from './Events'
import { getComposerConfirmationMessage } from './GetComposerConfirmationMessage'
import type {
  ApprovalMode,
  SavedPostRef,
} from '~publish/components/Composer/save/types'
import type { ComposerQueueingType } from '../stores/types'
import type { ScheduleOption } from '../types'

const AppHooks = {
  handleSavedDrafts: (args: {
    fromIdea: boolean // TODO: Review is this needed?
    entityName: 'post' | 'thread'
    /**
     * The saved post, when this save produced one the toast can link to. Absent
     * for a channel-less draft save, and for any save that failed.
     */
    savedPost?: SavedPostRef
    channelIds: Array<string>
    deepLink?: string
    willCreateAnotherPost?: boolean
    draftMode: boolean
    queueingType: NonNullable<ComposerQueueingType>
    isPlural: boolean
    approvalMode?: ApprovalMode
    statusChange?: 'post' | 'draft'
    scheduleSelectorChange?: ScheduleOption
  }): void => {
    const {
      fromIdea,
      entityName,
      draftMode,
      savedPost,
      queueingType,
      isPlural = false,
      approvalMode,
      statusChange,
      scheduleSelectorChange,
      willCreateAnotherPost,
    } = args

    // Currently, we only show a deep link if
    // there's a single post created.
    // Longer term, we'll want to figure out how to
    // show a deep link for multiple posts/post group.
    // We also need to determine whether its being saved
    // as a draft or a post
    let tab = 'queue'
    let entityType = 'post'

    if (savedPost?.status === 'needs_approval') {
      tab = 'approvals'
      entityType = 'draft'
    } else if (savedPost?.status === 'draft') {
      tab = 'drafts'
      entityType = 'draft'
    } else if (savedPost?.status === 'sent') {
      tab = 'sent'
    }

    const deepLink =
      !isPlural && savedPost
        ? `/channels/${savedPost.channelId}/posts/${savedPost.id}?tab=${tab}`
        : undefined

    const message = getComposerConfirmationMessage({
      entityName,
      draftMode,
      queueingType,
      savedPostStatus: savedPost?.status,
      isPlural,
      approvalMode,
      statusChange,
      scheduleSelectorChange,
    })

    const actions: ToastAction[] = []

    if (deepLink) {
      actions.push({
        label: `View ${entityType}`,
        onClick: (): void => {
          routerNavigate(deepLink)
        },
      })
    }

    if (!willCreateAnotherPost) {
      toast.success(message, { actions })
    }
    // The composer saves via the legacy RPC, so no Pusher event reaches this
    // client to refresh the persistent post-counts cache. Refresh mounted
    // consumers directly so queue/draft count badges update without a page
    // reload. Note: only refreshes consumers mounted now, not the cache — see
    // refetchAllPostCounts for the residual gap (CORE-4985).
    refetchAllPostCounts()

    events.emit('saved-drafts', { message, fromIdea })
  },
}

export default AppHooks
